import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";

import { EVA_OBJECT_TAGS, TagPreset } from "src/app/shared/constants/tag-presets";

import { Task, TaskAssignee, TaskObjectLink, TaskStatus } from "../domains/task";
import { TaskLot } from "../domains/task-lot";
import { TaskLotService } from "../services/task-lot.service";
import { TaskService } from "../services/task.service";
import { AssigneeItem } from "../task-assignee-picker/task-assignee-picker.component";
import { SelectOption, SelectOptionAvatar } from "src/app/shared/ds/eva-select/eva-select.component";
import { onDocumentClickCapture } from "src/app/shared/ds/utils/listen-outside-angular";

const OBJECT_TYPE_TO_EVA: Record<string, string> = {
  CUSTOMER: "CLIENT",
  PROJECT: "CHANTIER",
  INTERVENTION: "INTERVENTION",
  SALE: "VENTE",
  EXPENSE: "DEPENSE",
  INVOICE: "FINANCE",
  OFFER: "OFFRE",
  EMPLOYEE: "COLLABORATEUR",
  SUPPLIER: "FOURNISSEUR",
  PURCHASE_ORDER: "BON_COMMANDE",
  ARTICLE: "ARTICLE",
  CONTACT: "CONTACT",
};

@Component({
  selector: "app-task-lot-row",
  templateUrl: "./task-lot-row.component.html",
  styleUrls: ["./task-lot-row.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaskLotRowComponent implements OnChanges {
  private destroyRef = inject(DestroyRef);
  private host = inject<ElementRef<HTMLElement>>(ElementRef);
  private cdr = inject(ChangeDetectorRef);
  private taskLotService = inject(TaskLotService);
  private taskService = inject(TaskService);

  @Input({ required: true }) lot: TaskLot;
  @Input() tasks: Task[] = [];
  @Input() employees: any[] = [];
  @Input() contextObjectLink: TaskObjectLink | null = null;
  @Input() showObjectLink = true;
  @Input() compact = false;
  @Input() selectionMode = false;
  @Input() selectedTaskIds: Set<string> = new Set();
  @Input() editingTaskId: string | null = null;
  @Input() autoFocus = false;
  @Input() enableDragDrop = false;
  @Input() showAssignees = true;
  @Input() color = "";
  @Input() connectedDropLists: string[] = [];
  @Input() availableLots: TaskLot[] = [];

  @Output() lotRenamed = new EventEmitter<{ lotId: string; title: string }>();
  @Output() lotDeleted = new EventEmitter<string>();
  @Output() taskStatusChange = new EventEmitter<{ task: Task; done: boolean; status: TaskStatus }>();
  @Output() taskClick = new EventEmitter<Task>();
  @Output() taskEdited = new EventEmitter<Task>();
  @Output() editCancelled = new EventEmitter<void>();
  @Output() taskDeleted = new EventEmitter<void>();
  @Output() taskRemovedFromLot = new EventEmitter<string>();
  @Output() selectionToggle = new EventEmitter<Task>();
  @Output() contextMenuRequested = new EventEmitter<{ task: Task; x: number; y: number; alignRight?: boolean }>();
  @Output() taskDroppedOnLot = new EventEmitter<{ task: Task; lotId: string; index: number }>();
  @Output() addTaskRequested = new EventEmitter<string>();
  @Output() taskMenuAction = new EventEmitter<{ task: Task; action: string }>();
  @Output() markAllDoneRequested = new EventEmitter<string[]>();
  @Output() tasksReordered = new EventEmitter<{ lotId: string; taskIds: string[] }>();
  @Output() moveToLotRequested = new EventEmitter<{ task: Task; targetLotId: string }>();

  taskMoveMenuId: string | null = null;
  taskMoveMenuPosition = { top: 0, bottom: 0, right: 0 };
  taskMoveMenuOpensUpward = false;

  collapsed = false;
  editing = false;
  editTitle = "";
  showLotMenu = false;
  displayTasks: Task[] = [];
  lotMenuPosition = { top: 0, bottom: 0, right: 0 };
  lotMenuOpensUpward = false;
  private _ignoreNextDocumentClick = false;

  /** N'accepte que les drags de tâches (pas les drags de réordonnancement de lots) */
  readonly onlyAcceptTasks = (drag: { data: any }) => drag.data?.task_id !== undefined;

  // Dialog "Modifier le lot"
  showEditDialog = false;
  editSaving = false;
  editForm: { title: string; assignees: AssigneeItem[] } = { title: "", assignees: [] };

  // Dialog "Appliquer aux tâches"
  showApplyDialog = false;
  pendingLotTitle = "";
  pendingLotAssigneeItems: AssigneeItem[] = [];
  private pendingLotAssignees: TaskAssignee[] = [];

  get lotPercent(): number {
    if (this.tasks.length === 0) return 0;
    const done = this.tasks.filter((t) => t.status === "DONE" || t.status === "CANCELLED").length;
    return Math.round((done / this.tasks.length) * 100);
  }

  get doneCount(): number {
    return this.tasks.filter((t) => t.status === "DONE" || t.status === "CANCELLED").length;
  }

  private initialized = false;

  /** Focus et sélection du nom à l'ouverture du champ : on tape directement le nouveau nom */
  @ViewChild("titleInput") set titleInput(ref: ElementRef<HTMLInputElement> | undefined) {
    if (ref) setTimeout(() => ref.nativeElement.select());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["tasks"] || changes["lot"]) {
      // Reconstruire displayTasks depuis lot.task_ids (ordre authoritative) + this.tasks (objets)
      // Corrige le cas où this.tasks et this.lot.task_ids seraient désynchronisés
      const taskMap = new Map(this.tasks.map((t) => [t.task_id, t]));
      const ordered = (this.lot?.task_ids ?? []).map((id) => taskMap.get(id)).filter((t): t is Task => t !== undefined);
      this.displayTasks = ordered.length === this.tasks.length ? ordered : [...this.tasks];
    }
    if (changes["lot"] && !this.initialized) {
      this.editTitle = this.lot.title;
      this.initialized = true;
      if (this.autoFocus) {
        this.collapsed = false;
        this.editing = true;
      }
    } else if (changes["lot"] && !this.editing) {
      this.editTitle = this.lot.title;
    }
  }

  toggleCollapse(): void {
    this.collapsed = !this.collapsed;
    this.cdr.markForCheck();
  }

  startEditing(event: Event): void {
    event.stopPropagation();
    this.editing = true;
    this.editTitle = this.lot.title;
    this.cdr.markForCheck();
  }

  onTitleKeyDown(event: KeyboardEvent): void {
    // Les touches tapées dans le nom ne doivent pas déclencher les raccourcis de la liste
    event.stopPropagation();
    if (event.key === "Enter") {
      event.preventDefault();
      this.stopEditing();
    }
    if (event.key === "Escape") {
      // Comme les tâches : Échap quitte le champ sans perdre le nom tapé (enregistré à la sortie)
      this.stopEditing();
    }
  }

  /** Enregistre le nom à la sortie du champ (Entrée ou perte de focus), pas à chaque frappe */
  stopEditing(): void {
    if (!this.editing) return;
    this.editing = false;
    const title = this.editTitle.trim();
    if (title && title !== this.lot.title) {
      this.taskLotService
        .rename(this.lot.lot_id, title)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({ next: () => this.lotRenamed.emit({ lotId: this.lot.lot_id, title }) });
    } else {
      this.editTitle = this.lot.title;
    }
    this.cdr.markForCheck();
  }

  /** Clic à l'extérieur, en capture : le galet arrête la propagation des clics */
  private readonly stopOutsideClick = onDocumentClickCapture(
    () => this.showLotMenu || !!this.taskMoveMenuId,
    (event) => this.onDocumentClick(event),
  );

  onDocumentClick(event: MouseEvent): void {
    // Le bouton ⋮ du lot et son menu gèrent eux-mêmes la fermeture
    if (this.host.nativeElement.querySelector(".task-lot-row__menu")?.contains(event.target as Node)) return;
    if (this._ignoreNextDocumentClick) {
      this._ignoreNextDocumentClick = false;
      return;
    }
    let changed = false;
    if (this.showLotMenu) {
      this.showLotMenu = false;
      changed = true;
    }
    if (this.taskMoveMenuId) {
      this.taskMoveMenuId = null;
      changed = true;
    }
    if (changed) this.cdr.markForCheck();
  }

  toggleLotMenu(event: Event): void {
    event.stopPropagation();
    if (!this.showLotMenu) {
      // Fermer tous les autres menus ouverts dans l'app
      this._ignoreNextDocumentClick = true;
      document.body.dispatchEvent(new MouseEvent("click", { bubbles: true }));
      this._ignoreNextDocumentClick = false;

      const btn = event.currentTarget as HTMLElement;
      const rect = btn.getBoundingClientRect();
      const MENU_HEIGHT = 165;
      this.lotMenuOpensUpward = window.innerHeight - rect.bottom < MENU_HEIGHT + 8;
      this.lotMenuPosition = {
        top: rect.bottom + 4,
        bottom: window.innerHeight - rect.top + 4,
        right: window.innerWidth - rect.right,
      };
    }
    this.showLotMenu = !this.showLotMenu;
    this.cdr.markForCheck();
  }

  openEditDialog(event: Event): void {
    event.stopPropagation();
    this.showLotMenu = false;

    const seenKeys = new Set<string>();
    const merged: AssigneeItem[] = [];

    const collect = (assignees: { type: string; employee_id?: string; name?: string }[]) => {
      for (const a of assignees) {
        const key = a.employee_id || a.name || "";
        if (!key || seenKeys.has(key)) continue;
        seenKeys.add(key);
        const emp = this.employees.find((e) => e.employee_id === a.employee_id);
        merged.push({
          type: a.type as "employee" | "external",
          employee_id: a.employee_id,
          name: a.name ?? (emp ? `${emp.first_name || ""} ${emp.last_name || ""}`.trim() : ""),
          badge_color: emp?.badge_color || "#BEBEBE",
          initials: emp?.initials?.trim() || undefined,
        });
      }
    };

    collect(this.lot.assignees ?? []);
    this.tasks.forEach((t) => collect(t.assigned_to ?? []));

    this.editForm = { title: this.lot.title, assignees: merged };
    this.showEditDialog = true;
    this.cdr.markForCheck();
  }

  submitEdit(): void {
    const title = this.editForm.title.trim();
    if (!title) return;

    const assignees: TaskAssignee[] = this.editForm.assignees.map((a) => ({
      type: a.type,
      employee_id: a.employee_id,
      name: a.name,
    }));

    // Si des collaborateurs sont sélectionnés et qu'il y a des tâches → dialog intermédiaire
    if (assignees.length > 0 && this.tasks.length > 0) {
      this.pendingLotTitle = title;
      this.pendingLotAssigneeItems = [...this.editForm.assignees];
      this.pendingLotAssignees = assignees;
      this.showEditDialog = false;
      this.showApplyDialog = true;
      this.cdr.markForCheck();
      return;
    }

    this.saveLot(title, assignees);
  }

  applyCollaborators(mode: "merge" | "replace"): void {
    this.showApplyDialog = false;
    const title = this.pendingLotTitle;
    const assignees = this.pendingLotAssignees;

    this.saveLot(title, assignees);

    this.tasks.forEach((task) => {
      let newAssignees: TaskAssignee[];
      if (mode === "merge") {
        const existing = task.assigned_to || [];
        const existingIds = new Set(existing.map((a) => a.employee_id).filter(Boolean));
        newAssignees = [...existing, ...assignees.filter((a) => !existingIds.has(a.employee_id))];
      } else {
        newAssignees = assignees;
      }
      this.taskService
        .updateTaskAssignment(task.task_id, newAssignees)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (updated) => {
            task.assigned_to = updated.assigned_to;
            this.cdr.markForCheck();
          },
        });
    });
  }

  cancelApplyDialog(): void {
    this.showApplyDialog = false;
    this.pendingLotTitle = "";
    this.pendingLotAssigneeItems = [];
    this.pendingLotAssignees = [];
    this.cdr.markForCheck();
  }

  private saveLot(title: string, assignees: TaskAssignee[]): void {
    this.editSaving = true;
    this.cdr.markForCheck();
    this.taskLotService
      .update(this.lot.lot_id, { title, assignees })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.showEditDialog = false;
          this.editSaving = false;
          this.lotRenamed.emit({ lotId: this.lot.lot_id, title });
          this.cdr.markForCheck();
        },
        error: () => {
          this.editSaving = false;
          this.cdr.markForCheck();
        },
      });
  }

  deleteLotFromDialog(event: Event): void {
    this.showEditDialog = false;
    this.deleteLot(event);
  }

  openTaskMoveMenu(task: Task, event: Event): void {
    event.stopPropagation();
    if (this.taskMoveMenuId === task.task_id) {
      this.taskMoveMenuId = null;
    } else {
      // Fermer tous les autres menus ouverts
      this._ignoreNextDocumentClick = true;
      document.body.dispatchEvent(new MouseEvent("click", { bubbles: true }));
      this._ignoreNextDocumentClick = false;

      const btn = event.currentTarget as HTMLElement;
      const rect = btn.getBoundingClientRect();
      const MOVE_MENU_HEIGHT = 44 + this.otherLots.length * 36;
      this.taskMoveMenuOpensUpward = window.innerHeight - rect.bottom < MOVE_MENU_HEIGHT + 8;
      this.taskMoveMenuPosition = {
        top: rect.bottom + 4,
        bottom: window.innerHeight - rect.top + 4,
        right: window.innerWidth - rect.right,
      };
      this.taskMoveMenuId = task.task_id;
    }
    this.cdr.markForCheck();
  }

  moveTaskToLot(task: Task, targetLotId: string, event: Event): void {
    event.stopPropagation();
    this.taskMoveMenuId = null;
    this.moveToLotRequested.emit({ task, targetLotId });
    this.cdr.markForCheck();
  }

  removeTaskFromCurrentLot(task: Task, event: Event): void {
    event.stopPropagation();
    this.taskMoveMenuId = null;
    this.removeTaskFromLot(task.task_id, event);
  }

  get employeeOptions(): SelectOption[] {
    return this.employees.map((emp) => {
      const initials = emp.initials?.trim() || `${emp.first_name?.[0] || ""}${emp.last_name?.[0] || ""}`.toUpperCase();
      const avatar: SelectOptionAvatar = { initials, color: emp.badge_color || "#BEBEBE" };
      return { label: `${emp.first_name || ""} ${emp.last_name || ""}`.trim(), value: emp.employee_id, avatar };
    });
  }

  get editFormAssigneeIds(): string[] {
    return (this.editForm.assignees ?? []).map((a) => a.employee_id).filter(Boolean) as string[];
  }

  onEditFormAssigneesChange(ids: string[]): void {
    this.editForm.assignees = (ids ?? []).map((id) => {
      const emp = this.employees.find((e) => e.employee_id === id);
      return {
        type: "employee" as const,
        employee_id: id,
        name: emp ? `${emp.first_name || ""} ${emp.last_name || ""}`.trim() : id,
        badge_color: emp?.badge_color || "#BEBEBE",
        initials: emp?.initials?.trim() || undefined,
      };
    });
    this.cdr.markForCheck();
  }

  get otherLots(): TaskLot[] {
    return this.availableLots.filter((l) => l.lot_id !== this.lot.lot_id);
  }

  requestAddTask(event: Event): void {
    event.stopPropagation();
    this.showLotMenu = false;
    this.collapsed = false;
    this.addTaskRequested.emit(this.lot.lot_id);
    this.cdr.markForCheck();
  }

  requestMarkAllDone(event: Event): void {
    event.stopPropagation();
    this.showLotMenu = false;
    this.markAllDoneRequested.emit(this.tasks.map((t) => t.task_id));
    this.cdr.markForCheck();
  }

  deleteLot(event: Event): void {
    event.stopPropagation();
    this.showLotMenu = false;
    this.taskLotService
      .delete(this.lot.lot_id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: () => this.lotDeleted.emit(this.lot.lot_id) });
  }

  removeTaskFromLot(taskId: string, event: Event): void {
    event.stopPropagation();
    this.taskLotService
      .removeTaskFromLot(this.lot.lot_id, taskId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: () => this.taskRemovedFromLot.emit(taskId) });
  }

  getCommonObjectLink(): TaskObjectLink | null {
    const tasksWithLinks = this.tasks.filter((t) => t.objects_links && t.objects_links.length > 0);
    if (tasksWithLinks.length > 0) {
      const firstLinks = tasksWithLinks[0].objects_links;
      for (const link of firstLinks) {
        const key = `${link.object_type}:${link.object_id}`;
        if (tasksWithLinks.every((t) => t.objects_links.some((l) => `${l.object_type}:${l.object_id}` === key))) {
          return link;
        }
      }
    }
    return this.contextObjectLink ?? null;
  }

  getObjectLinkPreset(link: TaskObjectLink): TagPreset {
    const evaKey = OBJECT_TYPE_TO_EVA[link.object_type?.toUpperCase()] ?? link.object_type;
    const base = EVA_OBJECT_TAGS[evaKey] ?? { label: link.object_type, severity: "secondary" as const };
    return { ...base, label: link.object_name || base.label };
  }

  getLotDateRange(): { min: Date; max: Date } | null {
    const dates: Date[] = [];
    for (const task of this.tasks) {
      if (task.planned_start_date) dates.push(new Date(task.planned_start_date));
      if (task.actual_start_date) dates.push(new Date(task.actual_start_date));
      if (task.due_date) dates.push(new Date(task.due_date));
    }
    if (dates.length === 0) return null;
    const min = new Date(Math.min(...dates.map((d) => d.getTime())));
    const max = new Date(Math.max(...dates.map((d) => d.getTime())));
    return min.getTime() === max.getTime() ? null : { min, max };
  }

  formatShortDate(date: Date): string {
    const d = new Date(date);
    return d.getDate().toString().padStart(2, "0") + "." + (d.getMonth() + 1).toString().padStart(2, "0");
  }

  /** Indique qu'un drag est actuellement au-dessus de ce lot */
  isReceiving = false;

  get dropListId(): string {
    return `lot-drop-${this.lot.lot_id}`;
  }

  onDragEntered(): void {
    this.isReceiving = true;
    this.cdr.markForCheck();
  }

  onDragExited(): void {
    this.isReceiving = false;
    this.cdr.markForCheck();
  }

  moveTaskInLot(task: Task, direction: -1 | 1): void {
    const idx = this.displayTasks.findIndex((t) => t.task_id === task.task_id);
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= this.displayTasks.length) return;
    const reordered = [...this.displayTasks];
    moveItemInArray(reordered, idx, newIdx);
    const newTaskIds = reordered.map((t) => t.task_id);
    this.tasksReordered.emit({ lotId: this.lot.lot_id, taskIds: newTaskIds });
    this.taskLotService.update(this.lot.lot_id, { task_ids: newTaskIds }).subscribe();
  }

  onDropOnLot(event: CdkDragDrop<Task[]>): void {
    this.isReceiving = false;
    if (event.previousContainer === event.container) {
      if (event.previousIndex === event.currentIndex) return;
      // displayTasks est garanti cohérent avec lot.task_ids via ngOnChanges
      const reordered = [...this.displayTasks];
      moveItemInArray(reordered, event.previousIndex, event.currentIndex);
      const newTaskIds = reordered.map((t) => t.task_id);
      this.tasksReordered.emit({ lotId: this.lot.lot_id, taskIds: newTaskIds });
      this.taskLotService.update(this.lot.lot_id, { task_ids: newTaskIds }).subscribe();
      return;
    }
    const task: Task = event.item.data;
    if (!task?.task_id) return;
    this.collapsed = false;
    this.cdr.markForCheck();
    // index = position de drop dans le lot, pour insérer la tâche au bon endroit
    this.taskDroppedOnLot.emit({ task, lotId: this.lot.lot_id, index: event.currentIndex });
  }
}
